import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import { styled } from '@mui/material/styles';

interface FilterOption {
  label: string;
  value: any;
}

interface FilterButtonProps {
  label: string;
  options?: FilterOption[];
  value: any;
  onChange: (value: any) => void;
  isSearch?: boolean;
  onSearch?: (term: string) => void;
  searchPlaceholder?: string;
  enableOptionSearch?: boolean;
  /** Increment (e.g. from Cmd/Ctrl+F) to open search mode and focus the input. Skip 0 on first paint. */
  focusNonce?: number;
}

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSearch' && prop !== 'hasText' && prop !== 'hasValue',
})<{ isSearch?: boolean; hasText?: boolean; hasValue?: boolean }>(({ theme, isSearch, hasText, hasValue }) => {
  const searchActive = isSearch && hasText;
  const filterActive = !isSearch && hasValue;
  const solidPrimary = filterActive;
  const searchActiveBg = searchActive ? theme.palette.primary.light : theme.palette.common.white;

  return {
    textTransform: 'none',
    fontWeight: 400,
    color: solidPrimary ? theme.palette.primary.contrastText : theme.palette.text.primary,
    borderColor: theme.palette.divider,
    backgroundColor: solidPrimary ? theme.palette.primary.main : searchActiveBg,
    borderRadius: '9999px',
    borderWidth: '1px',
    borderStyle: 'solid',
    ...(isSearch
      ? {
          '& .MuiButton-startIcon': {
            color: theme.palette.text.primary,
          },
        }
      : {}),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: solidPrimary
        ? theme.palette.primary.dark
        : searchActive
          ? theme.palette.primary.light
          : theme.palette.primary.light,
      color: solidPrimary ? theme.palette.primary.contrastText : theme.palette.text.primary,
      ...(isSearch
        ? {
            '& .MuiButton-startIcon': {
              color: theme.palette.text.primary,
            },
          }
        : {}),
    },
    '&.MuiButton-contained': {
      backgroundColor: solidPrimary ? theme.palette.primary.main : searchActiveBg,
      color: solidPrimary ? theme.palette.primary.contrastText : theme.palette.text.primary,
      boxShadow: 'none',
      borderWidth: '1px',
      borderStyle: 'solid',
      '&:hover': {
        backgroundColor: solidPrimary ? theme.palette.primary.dark : theme.palette.primary.light,
        boxShadow: 'none',
        ...(isSearch
          ? {
              '& .MuiButton-startIcon': {
                color: theme.palette.text.primary,
              },
            }
          : {}),
      },
    },
  };
});

const SearchTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '9999px',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    fontWeight: 400,
    '& .MuiOutlinedInput-input': {
      fontWeight: 400,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: theme.palette.text.primary,
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light,
      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: theme.palette.text.primary,
      },
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text.primary,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: theme.palette.text.primary,
      },
    },
  },
}));

export const FilterButton = ({
  label,
  options = [],
  value,
  onChange,
  isSearch = false,
  onSearch,
  searchPlaceholder = 'Search...',
  enableOptionSearch = false,
  focusNonce,
}: FilterButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [optionSearchTerm, setOptionSearchTerm] = useState('');
  const lastSearchFocusNonce = useRef(-1);

  useEffect(() => {
    if (!isSearch || !isSearchMode || !searchInputRef.current) return;
    searchInputRef.current.focus();
    searchInputRef.current.select();
  }, [isSearch, isSearchMode, focusNonce]);

  useEffect(() => {
    if (!isSearch || focusNonce === undefined || focusNonce === 0) return;
    if (focusNonce === lastSearchFocusNonce.current) return;
    lastSearchFocusNonce.current = focusNonce;
    setSearchTerm(String(value ?? ''));
    setIsSearchMode(true);
  }, [focusNonce, isSearch, value]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isSearch) {
      setSearchTerm(String(value ?? ''));
      setIsSearchMode(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOptionSearchTerm('');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);
    if (onSearch) {
      onSearch(term);
    }
  };

  const handleSearchBlur = () => {
    if (!searchTerm) {
      setIsSearchMode(false);
    }
  };

  const selectedOption = options.find(option => option.value === value);
  const filteredOptions = options.filter(option => {
    const labelText = (option.label ?? '').toString().toLowerCase();
    const term = optionSearchTerm.toLowerCase();
    return labelText.includes(term);
  });

  if (isSearch && isSearchMode) {
    return (
      <SearchTextField
        value={searchTerm}
        onChange={handleSearchChange}
        onBlur={handleSearchBlur}
        placeholder={searchPlaceholder}
        inputRef={searchInputRef}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        size="small"
      />
    );
  }

  return (
    <>
      <StyledButton
        isSearch={isSearch}
        hasText={isSearch && !!value}
        hasValue={!isSearch && value !== null}
        variant={value !== null ? "contained" : "outlined"}
        onClick={handleClick}
        startIcon={isSearch ? <SearchIcon /> : undefined}
        endIcon={value !== null && !isSearch ? (
          <CancelIcon
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
          />
        ) : undefined}
      >
        {selectedOption ? selectedOption.label : label}
      </StyledButton>

      {!isSearch && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <List sx={{ minWidth: 240 }}>
            {enableOptionSearch && (
              <ListItem>
                <TextField
                  value={optionSearchTerm}
                  onChange={(e) => setOptionSearchTerm(e.target.value)}
                  placeholder="Search..."
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </ListItem>
            )}
            {filteredOptions.map((option) => (
              <ListItem key={String(option.value)} disablePadding>
                <ListItemButton onClick={() => {
                  onChange(option.value);
                  handleClose();
                }}>
                  <Checkbox
                    edge="start"
                    checked={value === option.value}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText primary={option.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Popover>
      )}
    </>
  );
};